<template>
  <transition name="modal">
    <div v-if="shouldShowBanner" class="verification-modal">
      <div class="verification-modal__backdrop" @click="dismissBanner"></div>
      <div class="verification-modal__container">
        <button 
          @click="dismissBanner" 
          class="verification-modal__close"
          aria-label="Close modal"
        >
          ×
        </button>
        <div class="verification-modal__content">
          <div class="verification-modal__icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
          </div>
          <h2 class="verification-modal__title">Identity Verification Required</h2>
          <p class="verification-modal__subtitle">To participate in the Ticket Exchange Program you must verify your identity using an option below. If not participating you can close this message box</p>
          <div class="verification-modal__buttons">
            <button
              @click="handleVerifyClick('dvs')"
              :disabled="loading"
              class="verification-modal__button verification-modal__button--document"
              aria-label="Verify with document"
            >
              {{ loading === 'dvs' ? 'Loading...' : 'Verify with Document' }}
            </button>
            <button
              @click="handleVerifyClick('bank')"
              :disabled="loading"
              class="verification-modal__button verification-modal__button--bank"
              aria-label="Verify with bank"
            >
              {{ loading === 'bank' ? 'Loading...' : 'Verify with Bank' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex';
import api from '../../api';

export default {
  name: 'VerificationBanner',
  data() {
    return {
      loading: null,
      dismissed: false
    };
  },
  computed: {
    ...mapState('user', {
      user: state => state.user
    }),
    shouldShowBanner() {
      // Don't show if dismissed
      if (this.dismissed) {
        return false;
      }
      
      // Don't show if user is not logged in
      if (!this.user) {
        return false;
      }
      
      // Only show on the /my-account page (not on /my-account/preferences or other pages)
      if (this.$route.name !== 'myAccount') {
        return false;
      }
      
      // Plus/Plus+ users are not subject to identity verification
      const accountType = this.user.accountType
      if (accountType === 'Plus' || accountType === 'Plus+') {
        return false
      }

      // Show only when id_verified is 'pending'
      if (this.user.id_verified === 'pending') {
        return true;
      }
      
      // Hide for all other cases
      return false;
    }
  },
  watch: {
    shouldShowBanner(newVal) {
      // Prevent body scroll when modal is open
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  },
  beforeDestroy() {
    // Restore body scroll when component is destroyed
    document.body.style.overflow = '';
  },
  methods: {
    handleVerifyClick(verificationType) {
      this.loading = verificationType;

      api.bangeo.createIdentityVerification(verificationType)
        .then(response => {
          console.log('Identity verification response:', response);

          if (response.data && response.data.verification_url) {
            // Redirect user to the verification URL
            console.log('Redirecting to verification URL:', response.data.verification_url);
            console.log('Reference ID:', response.data.reference_id);
            window.location.href = response.data.verification_url;
          } else {
            // If no URL provided, just dismiss and refresh user data
            this.loading = null;
            this.dismissed = true;
            this.$store.dispatch('user/RETRIEVE_USER_DATA');
            console.warn('No verification URL in response');
          }
        })
        .catch(error => {
          this.loading = null;
          console.error('Identity verification error:', error);
          alert('Unable to start verification. Please try again.');
        });
    },
    dismissBanner() {
      this.dismissed = true;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~sass/_variables.scss';
@import "~sass/_config.scss";
@import "~sass/mixins/_media-query.scss";

.verification-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.verification-modal__backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
}

.verification-modal__container {
  position: relative;
  background-color: $col-neutral--0;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  z-index: 10001;
  padding: 40px 30px;
  text-align: center;

  @include maxq(tablet) {
    padding: 30px 20px;
    width: 95%;
  }
}

.verification-modal__close {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: $col-neutral--7;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color $ttime;
  border-radius: 50%;

  &:hover {
    color: $col-neutral--9;
    background-color: $col-neutral--2;
  }
}

.verification-modal__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.verification-modal__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: $col-warn--2;
  margin-bottom: 10px;

  svg {
    width: 64px;
    height: 64px;

    @include maxq(tablet) {
      width: 48px;
      height: 48px;
    }
  }
}

.verification-modal__title {
  font-size: 24px;
  font-weight: 600;
  color: $col-neutral--9;
  margin: 0;
  line-height: 1.3;

  @include maxq(tablet) {
    font-size: 20px;
  }
}

.verification-modal__verification-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;

  &:hover .verification-modal__verification-tooltip {
    visibility: visible;
    opacity: 1;
  }
}

.verification-modal__verification-tooltip {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  top: calc(100% + 8px);
  right: 50%;
  transform: translateX(50%);
  background: #333;
  color: white;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 400;
  white-space: nowrap;
  z-index: 10002;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: opacity 0.2s ease, visibility 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-bottom-color: #333;
  }
}

.verification-modal__button.verification-status--rejected {
  cursor: help;
}


.verification-modal__subtitle {
  font-size: 16px;
  color: $col-neutral--8;
  margin: 0;
  line-height: 1.5;
  font-weight: 600;

  @include maxq(tablet) {
    font-size: 14px;
  }
}

.verification-modal__message {
  font-size: 16px;
  color: $col-neutral--7;
  margin: 0;
  line-height: 1.5;

  @include maxq(tablet) {
    font-size: 14px;
  }
}

.verification-modal__buttons {
  display: flex;
  gap: 15px;
  margin-top: 10px;

  @include maxq(tablet) {
    flex-direction: column;
    width: 100%;
  }
}

.verification-modal__button {
  color: $col-neutral--0;
  border: none;
  padding: 14px 32px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color $ttime;
  min-width: 180px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @include maxq(tablet) {
    width: 100%;
    padding: 12px 20px;
    min-width: auto;
  }

  &--document {
    background-color: $col-warn--2;

    &:hover:not(:disabled) {
      background-color: $col-warn--1;
    }
  }

  &--bank {
    background-color: $col-warn--2;

    &:hover:not(:disabled) {
      background-color: $col-warn--1;
    }
  }
}

// Modal transition animations
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .verification-modal__container,
.modal-leave-active .verification-modal__container {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter .verification-modal__container,
.modal-leave-to .verification-modal__container {
  opacity: 0;
  transform: scale(0.9);
}

.modal-enter .verification-modal__backdrop,
.modal-leave-to .verification-modal__backdrop {
  opacity: 0;
}
</style>
